// src/services/API/RpcProxyClient.ts
export type Fetcher = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

type Options = {
  baseUrl: string;
  getToken: () => Promise<string>;
  fetchImpl?: Fetcher;
  debug?: boolean;
};

type FnProxy = Record<string, (...args: any[]) => Promise<any>>;

function isNdjsonResponse (res: Response) {
  const ct = res.headers.get('content-type') || ''
  return ct.toLowerCase().includes('application/x-ndjson')
}

async function readNdjson (res: Response, debug, streamOptions) {
  if (!res.body) {
    const text = await res.text()
    throw new Error(`Expected NDJSON stream but response body is empty${text ? `: ${text}` : ''}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')

  let buffer = ''
  const items = [] as any[]
  let meta = null as any
  let result
  let sawDone = false

  const onItem = streamOptions && typeof streamOptions.onItem === 'function' ? streamOptions.onItem : null
  const onMeta = streamOptions && typeof streamOptions.onMeta === 'function' ? streamOptions.onMeta : null
  const onResult = streamOptions && typeof streamOptions.onResult === 'function' ? streamOptions.onResult : null
  const onDone = streamOptions && typeof streamOptions.onDone === 'function' ? streamOptions.onDone : null
  const collect = !(streamOptions && streamOptions.collect === false)

  const onLine = (line) => {
    if (!line) {
      return { done: false }
    }

    let msg
    try {
      msg = JSON.parse(line)
    } catch (e) {
      const err = new Error(`Invalid NDJSON line: ${line}`)
      err.name = 'RpcStreamParseError'
      throw err
    }

    const type = msg?.type

    if (type === 'ack') {
      return { done: false }
    }

    if (type === 'item') {
      const value = msg?.value
      if (onItem) {
        try { onItem(value) } catch { /* ignore */ }
      }
      if (collect) {
        items.push(value)
      }
      return { done: false }
    }

    if (type === 'meta') {
      meta = msg?.value ?? null
      if (onMeta) {
        try { onMeta(meta) } catch { /* ignore */ }
      }
      return { done: false }
    }

    if (type === 'result') {
      result = msg?.value
      if (onResult) {
        try { onResult(result) } catch { /* ignore */ }
      }
      return { done: false }
    }

    if (type === 'error') {
      const err = new Error(msg?.error || 'Stream error')
      err.name = 'RpcStreamError'
      throw err
    }

    if (type === 'done') {
      sawDone = true
      if (onDone) {
        try { onDone({ meta, result }) } catch { /* ignore */ }
      }
      return { done: true }
    }

    const err = new Error(`Unknown NDJSON message type: ${String(type)}`)
    err.name = 'RpcStreamProtocolError'
    throw err
  }

  while (true) {
    const { value, done } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    if (debug) {
      const chunkSize = value ? value.length : 0
      // eslint-disable-next-line no-console
      console.log('[rpc] NDJSON chunk received', { size: chunkSize })
    }

    while (true) {
      const idx = buffer.indexOf('\n')
      if (idx === -1) {
        break
      }

      const line = buffer.slice(0, idx).trim()
      buffer = buffer.slice(idx + 1)

      const status = onLine(line)
      if (status.done) {
        break
      }
    }

    if (sawDone) {
      try {
        await reader.cancel()
      } catch {
        // ignore
      }
      break
    }
  }

  buffer += decoder.decode()

  const tail = buffer.trim()
  if (tail) {
    const status = onLine(tail)
    if (status.done) {
      sawDone = true
    }
  }

  if (!sawDone) {
    const err = new Error('NDJSON stream ended before done message was received')
    err.name = 'RpcStreamProtocolError'
    throw err
  }

  if (result !== undefined && items.length === 0) {
    return result
  }

  const out = { result: items } as any
  if (meta !== null) {
    out.meta = meta
  }
  return out
}

function detectStreamFlag (args) {
  if (!args || args.length === 0) {
    return { wantsStream: false, args, streamOptions: null }
  }

  const last = args[args.length - 1]

  if (last === true) {
    return { wantsStream: true, args: args.slice(0, -1), streamOptions: null }
  }

  if (last && typeof last === 'object' && last.stream === true) {
    return { wantsStream: true, args: args.slice(0, -1), streamOptions: last }
  }

  return { wantsStream: false, args, streamOptions: null }
}

export default class RpcProxyClient {
  private baseUrl: string;
  private getToken: () => Promise<string>;
  private fetch: Fetcher;
  private debug: boolean;

  constructor ({ baseUrl, getToken, fetchImpl, debug = false }: Options) {
    // eslint-disable-next-line no-console
    console.log('[RpcProxyClient] constructor')
    this.baseUrl = baseUrl.replace(/\/+$/, '')
    this.getToken = getToken
    this.fetch = fetchImpl ?? fetch.bind(window)
    this.debug = debug
  }

  private async callRPC<T> (fnName: string, rawArgs: unknown[]): Promise<T> {
    // eslint-disable-next-line no-console
    console.log('[RpcProxyClient] callRPC:', { fnName, args: rawArgs })

    const { wantsStream, args, streamOptions } = detectStreamFlag(rawArgs)

    const token = await this.getToken()
    const url = `${this.baseUrl}/functions/call`
    const payload = { name: fnName, arguments: args }

    // eslint-disable-next-line no-console
    if (this.debug) console.log('[rpc]', fnName, '→', url, payload, { wantsStream })

    let res
    try {
      res = await this.fetch(url, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          accept: wantsStream ? 'application/x-ndjson' : 'application/json',
          authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      throw new Error(`RPC fetch failed for ${fnName}: ${message}`)
    }

    if (!res.ok) {
      const text = await res.text()
      throw new Error(
        `API ${res.status} ${res.statusText}${text ? `: ${text}` : ''}`
      )
    }

    if (wantsStream || isNdjsonResponse(res)) {
      const out = await readNdjson(res, this.debug, streamOptions)
      // eslint-disable-next-line no-console
      if (this.debug) console.log('[RpcProxyClient] callRPC (stream out):', { fnName, out })
      return out as T
    }

    const text = await res.text()
    let out

    try {
      out = text ? (JSON.parse(text) as T) : (undefined as unknown as T)
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      throw new Error(`Invalid JSON response for ${fnName}: ${message}`)
    }

    // eslint-disable-next-line no-console
    console.log('[RpcProxyClient] callRPC (out):', { fnName, out })
    return out
  }

  /** Realm-like: this.client.functions.<AnyName>(...args) */
  public readonly functions: FnProxy = new Proxy<FnProxy>({} as any, {
    get: (_target, prop) => {
      if (prop === 'then' || prop === 'toJSON' || prop === Symbol.toStringTag) return undefined
      if (typeof prop !== 'string') return undefined
      return (...args: unknown[]) => this.callRPC<any>(prop, args)
    }
  })

  /** Streamed RPC calls: this.client.fromStream.<AnyName>(...args) */
  public readonly fromStream: FnProxy = new Proxy<FnProxy>({} as any, {
    get: (_target, prop) => {
      if (prop === 'then' || prop === 'toJSON' || prop === Symbol.toStringTag) return undefined
      if (typeof prop !== 'string') return undefined
      return (...args: unknown[]) => {
        const last = args && args.length ? args[args.length - 1] : null
        if (last && typeof last === 'object' && (last as any).stream === true) {
          return this.callRPC<any>(prop, args)
        }
        return this.callRPC<any>(prop, [...args, true])
      }
    }
  })
}
